/**
 * IndexedDB chunk persistence for crash recovery.
 * Saves recording chunks every 30s so a tab crash doesn't lose content.
 */

const DB_NAME = "storyforge-recordings";
const STORE_NAME = "chunks";

export interface RecordingChunk {
  id: string; // "{projectId}-{sessionId}-{index}"
  projectId: string;
  sessionId: string;
  index: number;
  blob: Blob;
  mimeType: string;
  timestamp: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("projectId", "projectId");
        store.createIndex("sessionId", "sessionId");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveChunk(chunk: RecordingChunk): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(chunk);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function getChunksForSession(sessionId: string): Promise<RecordingChunk[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const index = tx.objectStore(STORE_NAME).index("sessionId");
    const request = index.getAll(sessionId);
    request.onsuccess = () => resolve((request.result as RecordingChunk[]).sort((a, b) => a.index - b.index));
    request.onerror = () => reject(request.error);
  });
}

export async function getChunksForProject(projectId: string): Promise<RecordingChunk[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const index = tx.objectStore(STORE_NAME).index("projectId");
    const request = index.getAll(projectId);
    request.onsuccess = () => resolve((request.result as RecordingChunk[]).sort((a, b) => a.index - b.index));
    request.onerror = () => reject(request.error);
  });
}

export function assembleChunks(chunks: RecordingChunk[]): Blob {
  const mimeType = chunks[0]?.mimeType ?? "audio/webm";
  return new Blob(chunks.map((c) => c.blob), { type: mimeType });
}

export async function clearSession(sessionId: string): Promise<void> {
  const chunks = await getChunksForSession(sessionId);
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  for (const chunk of chunks) store.delete(chunk.id);
  return new Promise((resolve) => { tx.oncomplete = () => resolve(); });
}

export async function clearProject(projectId: string): Promise<void> {
  const chunks = await getChunksForProject(projectId);
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  for (const chunk of chunks) store.delete(chunk.id);
  return new Promise((resolve) => { tx.oncomplete = () => resolve(); });
}

export async function hasOrphanedChunks(projectId: string): Promise<boolean> {
  const chunks = await getChunksForProject(projectId);
  return chunks.length > 0;
}
